import React, {useCallback, useContext, useState} from "react";
import {AppFunctionsContext} from "../App";
import {useMutation, useQueryClient} from "react-query";
import {markReadyToInvoice} from "../functions/api";
import {toastMessages} from "../hooks/useToastMessages";
import {InnerCellWrapper} from "../Components/ReactTable";
import {Button} from "../Components/Button";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import * as fas from "@fortawesome/pro-solid-svg-icons";
import * as fal from "@fortawesome/pro-light-svg-icons";
import {formatNumber} from "../functions/shared";
import dayjs from "dayjs";

const paymentMethods = [
    {value: "CC_PORTAL", label: "Credit card (paid via portal)"},
    {value: "CC_IN_PERSON", label: "Credit card (in person)"},
    {value: "CASH", label: "Cash"},
    {value: "CHECK", label: "Check"},
    {value: "BANK", label: "Bank transfer"},
    {value: "OTHER", label: "Other"},
];

const markReadyOverlay = "mark-ready-to-invoice-overlay";

export const MarkReadyToInvoiceModal = ({contract, client}) => {
    const {addToOverlaySet, removeFromOverlaySet, closeModal} = useContext(AppFunctionsContext);
    const queryClient = useQueryClient();
    const mutation = useMutation({mutationFn: markReadyToInvoice, mutationKey: "markReadyToInvoice"});
    const defaultMethod = contract.paid ? "CC_PORTAL" : "CC_IN_PERSON";
    const [paymentMethod, setPaymentMethod] = useState(defaultMethod);

    const handleConfirm = useCallback(async () => {
        try {
            addToOverlaySet(markReadyOverlay);
            const ret = await mutation.mutateAsync({contractId: contract.id, paymentMethod});
            const {messageArray} = ret || {};
            toastMessages(messageArray, () => {
                queryClient.invalidateQueries(["getAdminMasterLists"]);
                closeModal();
            });
        } finally {
            removeFromOverlaySet(markReadyOverlay);
        }
    }, [contract.id, paymentMethod]);

    return (
        <div style={{maxWidth: 560, padding: 20, display: "flex", flexDirection: "column", gap: 16}}>
            <div style={{fontSize: 15, lineHeight: 1.5}}>
                This will create a draft invoice in EasyTax for <strong>{client?.name}</strong> totaling{" "}
                <strong>&#8362;{formatNumber(contract.total)}</strong>. The secretary will open EasyTax,
                review the draft, and finalize and send to the client.
            </div>
            <div style={{
                background: "#fff7e6",
                border: "1px solid #ffd591",
                borderRadius: 4,
                padding: 12,
                fontSize: 14,
                lineHeight: 1.4,
            }}>
                <strong>This action cannot be undone from the portal.</strong> Once the draft has been created,
                any changes or cancellation must be handled by the secretary directly in EasyTax.
            </div>

            <div>
                <div style={{fontWeight: "bold", marginBottom: 8}}>How was this invoice paid?</div>
                <div style={{display: "flex", flexDirection: "column", gap: 6}}>
                    {paymentMethods.map(opt => (
                        <label key={opt.value}
                               style={{display: "flex", alignItems: "center", gap: 8, cursor: "pointer"}}>
                            <input
                                type="radio"
                                name="paymentMethod"
                                value={opt.value}
                                checked={paymentMethod === opt.value}
                                onChange={() => setPaymentMethod(opt.value)}
                            />
                            <span>{opt.label}</span>
                        </label>
                    ))}
                </div>
            </div>

            <div style={{display: "flex", gap: 10, justifyContent: "flex-end"}}>
                <Button onClick={closeModal}>Cancel</Button>
                <Button onClick={handleConfirm}>Create Invoice</Button>
            </div>
        </div>
    );
};

export const MarkReadyToInvoiceCell = cell => {
    const {openModal} = useContext(AppFunctionsContext);
    const {row: {original: contract = {}} = {}, clientLookup} = cell;
    const client = clientLookup?.[contract.clientId];

    const openMarkReadyModal = useCallback(() => {
        openModal({
            header: "Mark ready to invoice",
            content: <MarkReadyToInvoiceModal contract={contract} client={client}/>,
        });
    }, [contract, client]);

    // A contract has been invoiced (and finalized) once it has an InvoiceNumber.
    // `invoicedExternalId` alone (without InvoiceNumber) means a draft exists but the
    // secretary hasn't produced it in EasyTax yet.
    if (contract.invoiceNumber) {
        return (
            <InnerCellWrapper>
                <span style={{color: "#2a7", display: "flex", alignItems: "center", gap: 6}}>
                    <FontAwesomeIcon icon={fas.faCheck}/> Invoice #{contract.invoiceNumber}
                </span>
            </InnerCellWrapper>
        );
    }

    if (contract.invoicedExternalId) {
        return (
            <InnerCellWrapper>
                <span style={{color: "#888", display: "flex", alignItems: "center", gap: 6}}>
                    <FontAwesomeIcon icon={fal.faHourglassHalf}/> Draft in EasyTax
                </span>
            </InnerCellWrapper>
        );
    }

    // Permanent failure reported by SyncApp. Admin gets a clear error and a retry button
    // that clears the error and re-queues the contract for the next sync.
    if (contract.invoiceError) {
        const attempt = contract.lastInvoiceAttempt
            ? dayjs(contract.lastInvoiceAttempt).format("DD MMM HH:mm")
            : null;
        return (
            <InnerCellWrapper>
                <div style={{display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start"}}>
                    <span
                        title={contract.invoiceError}
                        style={{
                            color: "#c62828",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontWeight: 600,
                            maxWidth: 220,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}>
                        <FontAwesomeIcon icon={fas.faTriangleExclamation}/>
                        {contract.invoiceError}
                    </span>
                    {attempt && (
                        <span style={{fontSize: 11, color: "#888"}}>Last attempt: {attempt}</span>
                    )}
                    <Button onClick={openMarkReadyModal} style={{fontSize: 12, padding: "2px 10px"}}>
                        <FontAwesomeIcon icon={fas.faRotateRight}/> Retry
                    </Button>
                </div>
            </InnerCellWrapper>
        );
    }

    if (contract.readyToInvoice) {
        return (
            <InnerCellWrapper>
                <span style={{color: "#888", display: "flex", alignItems: "center", gap: 6}}>
                    <FontAwesomeIcon icon={fal.faHourglassHalf}/> Pending sync
                </span>
            </InnerCellWrapper>
        );
    }

    const canInvoice = contract.paid || contract.allowAlternativePaymentPlan;
    return (
        <InnerCellWrapper>
            <Button
                onClick={canInvoice ? openMarkReadyModal : undefined}
                disabled={!canInvoice}
                title={canInvoice ? undefined : "Client must pay or have Allow Alternative Payment enabled before invoicing"}
                style={canInvoice ? undefined : {opacity: 0.5, cursor: "not-allowed"}}>
                <FontAwesomeIcon icon={fas.faFileInvoice}/> Mark ready to invoice
            </Button>
        </InnerCellWrapper>
    );
};
